import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import {
  authorLabel,
  describeEvent,
  isRestored,
  restoreOperation,
  reviewMoment,
} from '@/lib/review-labels';
import type { ReviewEvent } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';

/**
 * The Historique tab (relecture.md §10): every gesture, newest first - who,
 * when, what - read off the journal the server keeps. Nothing is rewritten
 * there; « Remettre » is a new gesture, a `range` rebuilt from the extract the
 * `unrange` line removed.
 *
 * « Remettre » is offered on a removal only while the passage is NOT in its case
 * again, and only while the review may be written.
 */

export interface ReviewHistoryProps {
  /** Undefined while the journal is being read. */
  readonly events: readonly ReviewEvent[] | undefined;
  readonly failed: boolean;
}

export function ReviewHistory({ events, failed }: ReviewHistoryProps) {
  const { apply, canWrite, busy, review, grid } = useReviewApi();

  if (failed) {
    return (
      <p role="alert" className="text-caption text-rouge">
        L’historique n’a pas pu être lu. Rechargez la page.
      </p>
    );
  }
  if (events === undefined) {
    return <p className="text-caption text-gray-500 italic">Lecture de l’historique…</p>;
  }
  if (events.length === 0) {
    return <p className="text-caption text-gray-500 italic">Aucun geste encore.</p>;
  }

  return (
    <ul className="flex flex-col text-caption">
      {events.map((event) => {
        const restore = restoreOperation(event);
        const restorable = restore !== null && !isRestored(restore, review.extracts);
        return (
          <li
            key={event.id}
            data-testid={REVIEW_TEST_IDS.event}
            data-op={event.op}
            className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-start gap-2 border-gray-100 border-b py-1.5"
          >
            <span className="text-gray-500 text-caption tabular-nums">
              {reviewMoment(event.at)}
            </span>
            <span className="min-w-0 break-words">
              <b className="font-semibold">{authorLabel(event.by)}</b> {describeEvent(event, grid)}
              {event.via === 'claude' ? (
                <>
                  {' '}
                  <span className="inline-flex items-center rounded-sm bg-bleu-ciel-tint px-1.5 font-semibold text-bleu-ciel-deep text-micro">
                    {event.dapres === null ? 'via Claude' : `via Claude · d’après ${event.dapres}`}
                  </span>
                </>
              ) : null}
            </span>
            {restorable && canWrite && restore !== null ? (
              <Button
                type="button"
                size="xs"
                compact={true}
                variant="field"
                data-testid={REVIEW_TEST_IDS.restore}
                disabled={busy}
                onClick={() => void apply([restore])}
              >
                Remettre
              </Button>
            ) : (
              <span />
            )}
          </li>
        );
      })}
    </ul>
  );
}
